import React, { useState, useRef, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  LayoutGrid, 
  Users, 
  GraduationCap,
  ClipboardList,
  LineChart,
  FileText,
  Calendar,
  DollarSign,
  Puzzle,
  LogOut,
  User,
  Landmark,
  Brain,
  CreditCard,
  Banknote,
  MessageCircle,
  ArrowRight,
  Wallet,
  Menu,
  X,
  TrendingUp,
  TrendingDown,
  Lock, 
  Eye, 
  EyeOff, 
  Check,
  Package
} from 'lucide-react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import Notificaciones from './notificaciones';
import Chatbot from './chatbot';
import Chat from './chat';
import { obtenerCentro } from '../services/centroService';
import { obtenerActividadesProfesor } from '../services/ActividadesService';
import { obtenerEvaluaciones } from '../services/evaluacionesService';
import { obtenerSugerencias } from '../services/chatbotService';
import { obtenerHistorialPagos, obtenerEgresos } from '../services/FinanzasService';
import { actualizarUsuarioPerfil } from '../services/matriculaService'; // Assuming this service can update admin users
import { obtenerTodosControlPagos } from '../services/pagosServices';
import useMessageNotifications from '../hooks/useMessageNotifications';
import moment from 'moment';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AdminDashboard = ({ user, handleLogout }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showFinanzasMenu, setShowFinanzasMenu] = useState(false);
  const [nombreCentro, setNombreCentro] = useState('Centro Infantil');
  const [actividadesProximas, setActividadesProximas] = useState([]);
  const [evaluacionesRecientes, setEvaluacionesRecientes] = useState([]);
  const [sugerenciasRecientes, setSugerenciasRecientes] = useState([]);
  const [transferenciaPendientes, setTransferenciasPendientes] = useState([]);
  const [loadingActividades, setLoadingActividades] = useState(true);
  const [loadingEvaluaciones, setLoadingEvaluaciones] = useState(true);
  const [loadingSugerencias, setLoadingSugerencias] = useState(true);
  const [loadingFinanzas, setLoadingFinanzas] = useState(true);
  const [ingresos, setIngresos] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const profileMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const finanzasMenuRef = useRef(null);
  const location = useLocation();
  const passwordIsCedula = localStorage.getItem('passwordIsCedula') === 'true';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [periodoMatriculas, setPeriodoMatriculas] = useState('');
  const [openSubMenus, setOpenSubMenus] = useState({});

  // State for Password Change Modal
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(passwordIsCedula);
  const [nuevoPassword, setNuevoPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,     // min 8 characters
    letter: false,     // min 1 letter (can be upper or lower)
    number: false,     // min 1 number
    special: false,    // min 1 special character
  });
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiResponseMessage, setApiResponseMessage] = useState({ text: '', type: '' });
  const [isPasswordSuccessfullyChanged, setIsPasswordSuccessfullyChanged] = useState(false);

  const {
    mensajesNoLeidos,
    showChat,
    handleChatOpen,
    handleChatClose,
    handleMessageRead
  } = useMessageNotifications(user);

  const toggleSubMenu = (menuKey) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // --- Password Change Modal Logic ---
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-zA-Z]/.test(password)) strength += 25; // Checks for any letter
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 50) return 'bg-red-500';
    if (strength < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 50) return 'Débil';
    if (strength < 75) return 'Media';
    return 'Fuerte';
  };

  const validatePasswordCriteria = (password) => ({
    length: password.length >= 8,
    letter: /[a-zA-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  });

  const PasswordRequirement = ({ text, met }) => (
    <div className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-slate-500'}`}>
      {met ? <Check size={14} className="mr-1.5 flex-shrink-0" /> : <X size={14} className="mr-1.5 flex-shrink-0" />}
      <span>{text}</span>
    </div>
  );

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    setApiResponseMessage({ text: '', type: '' }); // Clear previous API message

    if (nuevoPassword !== confirmarPassword) {
      setPasswordChangeError('Las contraseñas no coinciden.');
      return;
    }
    if (!isValidPassword || passwordStrength < 75) {
      setPasswordChangeError('La contraseña no cumple con todos los requisitos de seguridad o es demasiado débil.');
      return;
    }

    try {
      const userIdToUpdate = user && user.id ? user.id : JSON.parse(localStorage.getItem('user'))?.id;
      if (!userIdToUpdate) {
        setPasswordChangeError('No se pudo identificar al usuario para actualizar la contraseña.');
        console.error('User ID not found for password update.');
        return;
      }

      const response = await actualizarUsuarioPerfil(userIdToUpdate, { password_us: nuevoPassword });

      if (response.success || response.message === 'Usuario actualizado correctamente') {
        localStorage.setItem('passwordIsCedula', 'false');
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'passwordIsCedula',
          newValue: 'false'
        }));
        setApiResponseMessage({ text: 'Contraseña actualizada correctamente.', type: 'success' });
        setIsPasswordSuccessfullyChanged(true);
        setPasswordChangeError(''); // Clear any previous validation error
      } else {
        setApiResponseMessage({ text: response.message || 'Error al cambiar la contraseña. Intente de nuevo.', type: 'error' });
        setIsPasswordSuccessfullyChanged(false);
      }
    } catch (error) {
      console.error("Error en handlePasswordChangeSubmit:", error);
      setApiResponseMessage({ text: error.response?.data?.message || error.message || 'Ocurrió un error en el servidor. Por favor, intente más tarde.', type: 'error' });
      setIsPasswordSuccessfullyChanged(false);
    }
  };

  const handleCloseModalAfterSuccess = () => {
    setShowPasswordChangeModal(false);
    setNuevoPassword('');
    setConfirmarPassword('');
    setPasswordStrength(0);
    setPasswordCriteria({ length: false, letter: false, number: false, special: false });
    setApiResponseMessage({ text: '', type: '' });
    setPasswordChangeError('');
    setIsPasswordSuccessfullyChanged(false);
  };
  // --- End Password Change Modal Logic ---

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Su sesión ha expirado. Por favor, inicie sesión nuevamente.', {
          onClose: () => handleLogout()
        });
      }
    };

    checkTokenExpiration();
  }, [handleLogout]); // Dependencia handleLogout para que se ejecute si cambia

  useEffect(() => {
    const cargarNombreCentro = async () => {
      try {
        const response = await obtenerCentro();
        if (response.success && response.data.length > 0) {
          setNombreCentro(`Centro Infantil ${response.data[0].nombre_unidad}`);
        }
      } catch (error) {
        console.error('Error al cargar el nombre del centro:', error);
      }
    };

    cargarNombreCentro();
  }, []);

  useEffect(() => {
    const cargarActividadesProximas = async () => {
      try {
        const userId = JSON.parse(localStorage.getItem('user')).id;
        const response = await obtenerActividadesProfesor(userId);
        
        if (response.actividades) {
          const hoy = moment();
          const enUnaSemana = moment().add(7, 'days');
          
          const actividadesFiltradas = response.actividades.filter(actividad => {
            const fechaActividad = moment(actividad.fecha_programada);
            return fechaActividad.isBetween(hoy, enUnaSemana, 'day', '[]') && 
                   actividad.estado !== 'completada';
          });

          setActividadesProximas(actividadesFiltradas.slice(0, 5));
        }
      } catch (error) {
        console.error('Error al cargar actividades:', error);
      } finally {
        setLoadingActividades(false);
      }
    };

    const cargarEvaluacionesRecientes = async () => {
      try {
        const response = await obtenerEvaluaciones();
        if (response.success) {
          const evaluacionesOrdenadas = response.data
            .sort((a, b) => moment(b.fecha).valueOf() - moment(a.fecha).valueOf())
            .slice(0, 5);
          setEvaluacionesRecientes(evaluacionesOrdenadas);
        }
      } catch (error) {
        console.error('Error al cargar evaluaciones:', error);
      } finally {
        setLoadingEvaluaciones(false);
      }
    };

    const cargarSugerencias = async () => {
      try {
        const response = await obtenerSugerencias();
        if (response.success) {
          const sugerenciasOrdenadas = response.data
            .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
            .slice(0, 5);
          setSugerenciasRecientes(sugerenciasOrdenadas);
        }
      } catch (error) {
        console.error('Error al cargar sugerencias:', error);
      } finally {
        setLoadingSugerencias(false);
      }
    };

    const cargarTransferenciasPendientes = async () => {
      try {
        const response = await obtenerTodosControlPagos();
        if (response.success) {
          const transferenciasEnRevision = response.data.filter(pago => 
            pago.tipo_pago === 'transferencia' && 
            pago.estado_verificacion === 'Pendiente'
          );
          setTransferenciasPendientes(transferenciasEnRevision);
        }
      } catch (error) {
        console.error('Error al cargar transferencias pendientes:', error);
      }
    };

    const cargarDatosFinancieros = async () => {
      try {
        const [ingresosRes, egresosRes] = await Promise.all([
          obtenerHistorialPagos(),
          obtenerEgresos()
        ]);
        setIngresos(ingresosRes.historialPagos || []);
        setEgresos(egresosRes.egresos || []);
      } catch (error) {
        console.error("Error al cargar datos financieros:", error);
      } finally {
        setLoadingFinanzas(false);
      }
    };

    const actualizarPeriodoMatriculas = () => {
      const fechaActual = moment();
      const mesActual = fechaActual.format('MMMM');
      const mesSiguiente = fechaActual.add(1, 'month').format('MMMM');
      setPeriodoMatriculas(`${mesActual} - ${mesSiguiente}`);
    };

    if (location.pathname === '/admin') {
      cargarActividadesProximas();
      cargarEvaluacionesRecientes();
      cargarSugerencias();
      cargarDatosFinancieros();
      cargarTransferenciasPendientes();
      actualizarPeriodoMatriculas();
    }
  }, [location.pathname]);

  // useEffects for Password Change Modal
  useEffect(() => {
    setIsValidPassword(
      passwordCriteria.length &&
      passwordCriteria.letter &&
      passwordCriteria.number &&
      passwordCriteria.special
    );
  }, [passwordCriteria]);

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(nuevoPassword));
    setPasswordCriteria(validatePasswordCriteria(nuevoPassword));
  }, [nuevoPassword]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'passwordIsCedula') {
        const mustChange = event.newValue === 'true';
        setShowPasswordChangeModal(mustChange);
        if (mustChange) {
            setNuevoPassword('');
            setConfirmarPassword('');
            setPasswordChangeError('');
        }
      }
    };
    // Initialize modal state based on current localStorage, only if not already set by direct prop
    if (localStorage.getItem('passwordIsCedula') === 'true') {
        setShowPasswordChangeModal(true);
    }

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (finanzasMenuRef.current && !finanzasMenuRef.current.contains(event.target)) {
        setShowFinanzasMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const menuItems = [
    { icon: LayoutGrid, text: 'Inicio', href: '/admin' },
    { 
      icon: Landmark, 
      text: 'Mi Centro', 
      href: '/admin/centro',
      subItems: [
        { icon: FileText, text: 'Cartas de Compromiso', href: '/admin/centro/cartas' },
        { icon: MessageCircle, text: 'Sugerencias', href: '/admin/centro/sugerencias' },
        { icon: Package, text: 'Planes', href: '/admin/centro/planes' },
      ]
    },
    { icon: Users, text: 'Gestión de Usuarios', href: '/admin/usuarios' },
    { icon: GraduationCap, text: 'Gestión de Matrículas', href: '/admin/Matriculas' },
    { icon: ClipboardList, text: 'Gestión de Evaluaciones', href: '/admin/evaluaciones' },
    { icon: FileText, text: 'Reportes Evaluaciones', href: '/admin/reportes' },
    { 
      icon: DollarSign, 
      text: 'Gestión Financiera',
      href: '/admin/finanzas',
      subItems: [
        { icon: CreditCard, text: 'Transferencias', href: '/admin/transferencias' },
        { icon: Banknote, text: 'Pago Efectivo', href: '/admin/pago-efectivo' },
      ]
    },
    { icon: Brain, text: 'Gestión de Chatbot', href: '/admin/chatbot' }, 
  ];

  const MobileMenu = () => (
    <div className={`lg:hidden fixed inset-0 z-50 ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
      <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
      <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Menú</h2>
          <button onClick={() => setIsMobileMenuOpen(false)} className="p-2">
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {menuItems.map((item, index) => (
            <div key={index}>
              {item.subItems ? (
                <div>
                  <div className="flex">
                    <Link
                      to={item.href}
                      className="flex-1 flex items-center gap-3 px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.text}</span>
                    </Link>
                    <button
                      onClick={() => toggleSubMenu(item.text)}
                      className="px-2 flex items-center text-slate-600 hover:text-blue-800"
                    >
                      <ArrowRight className={`h-4 w-4 transition-transform duration-200 ${openSubMenus[item.text] ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                  <div className={`${openSubMenus[item.text] ? 'block' : 'hidden'} pl-4`}>
                    {item.subItems.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.href}
                        className="flex items-center gap-3 px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <subItem.icon className="h-4 w-4" />
                        <span>{subItem.text}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  to={item.href}
                  className="flex items-center gap-3 px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.text}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>
    </div>
  );

  const DashboardContent = () => {
    // Preparar datos para el gráfico de movimientos financieros
    const fechaInicio = moment().subtract(6, 'days');
    const fechas = [...new Array(7)].map((_, i) => 
      moment(fechaInicio).add(i, 'days')
    );

    // Procesar datos de ingresos y egresos
    const datosIngresos = fechas.map(fecha => {
      const ingresosDelDia = ingresos
        .filter(ingreso => moment(ingreso.fecha_pago).startOf('day').isSame(fecha.startOf('day')))
        .reduce((sum, ingreso) => sum + (parseFloat(ingreso.monto_pagado) || 0), 0);
      return Number(ingresosDelDia.toFixed(2));
    });

    const datosEgresos = fechas.map(fecha => {
      const egresosDelDia = egresos
        .filter(egreso => moment(egreso.fecha).startOf('day').isSame(fecha.startOf('day')))
        .reduce((sum, egreso) => sum + (parseFloat(egreso.monto) || 0), 0);
      return Number(egresosDelDia.toFixed(2));
    });

    // Configuración del gráfico
    const movimientosChartData = {
      labels: fechas.map(fecha => fecha.format('DD/MM')),
      datasets: [
        {
          label: 'Ingresos',
          data: datosIngresos,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Egresos',
          data: datosEgresos,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };

    const movimientosChartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: {
            boxWidth: 15,
            usePointStyle: true,
            padding: 15
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          padding: 10,
          callbacks: {
            label: function(context) {
              const value = context.parsed.y;
              return `${context.dataset.label}: $ ${value.toFixed(2)}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            callback: function(value) {
              return '$ ' + value.toFixed(2);
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };

    // Calcular totales
    const totalIngresosHoy = ingresos
      .filter(i => moment(i.fecha_pago).isSame(moment(), 'day'))
      .reduce((sum, i) => sum + (parseFloat(i.monto_pagado) || 0), 0);

    const totalIngresosSemana = ingresos
      .filter(i => moment(i.fecha_pago).isAfter(moment().subtract(7, 'days')))
      .reduce((sum, i) => sum + (parseFloat(i.monto_pagado) || 0), 0);

    const crecimientoPorcentaje = ((totalIngresosHoy / (totalIngresosSemana / 7)) * 100) - 100;

    return (
      <div className="space-y-6">
        {/* Sección de Resumen - Tarjetas Principales */}
        <section aria-labelledby="summary-heading">
          <h2 id="summary-heading" className="sr-only">Resumen del Dashboard</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Tarjeta de Periodo de Matrículas */}
            <div 
              role="region" 
              aria-label="Periodo de Matrículas"
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-emerald-200 focus-within:ring-2 focus-within:ring-emerald-400 focus-within:ring-offset-2"
              tabIndex="0"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm font-medium">Periodo de Matrículas</p>
                  <h3 className="text-xl font-bold mt-1">{periodoMatriculas}</h3>
                  <p className="text-emerald-100 text-xs mt-1">Periodo actual de cobro</p>
                </div>
                <Calendar className="w-12 h-12 text-emerald-100 opacity-80" aria-hidden="true" />
              </div>
            </div>

            {/* Tarjeta de Transferencias Pendientes */}
            <div 
              role="region" 
              aria-label="Transferencias Pendientes"
              className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-violet-200 focus-within:ring-2 focus-within:ring-violet-400 focus-within:ring-offset-2"
              tabIndex="0"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-violet-100 text-sm font-medium">Transferencias Pendientes</p>
                  <h3 className="text-3xl font-bold mt-1">
                    {ingresos.filter(ingreso => ingreso.estado === 'pendiente').length}
                  </h3>
                  <p className="text-violet-100 text-xs mt-1">Pagos por verificar</p>
                </div>
                <CreditCard className="w-12 h-12 text-violet-100 opacity-80" aria-hidden="true" />
              </div>
            </div>

            {/* Tarjeta de Sugerencias */}
            <div 
              role="region" 
              aria-label="Sugerencias Nuevas"
              className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-6 text-white transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-indigo-200 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:ring-offset-2"
              tabIndex="0"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm font-medium">Sugerencias</p>
                  <h3 className="text-3xl font-bold mt-1">{sugerenciasRecientes.length}</h3>
                  <p className="text-indigo-100 text-xs mt-1">Nuevas sugerencias</p>
                </div>
                <MessageCircle className="w-12 h-12 text-indigo-100 opacity-80" aria-hidden="true" />
              </div>
            </div>
          </div>
        </section>

        {/* Sección de Detalles */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Panel de Movimientos Financieros */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <h2 className="text-xl font-bold text-gray-800">Movimientos Financieros</h2>
                </div>
                <Link 
                  to="/admin/finanzas"
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors duration-200 text-sm font-medium"
                >
                  Ver detalles
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">Ingresos de Hoy</p>
                    <div className={`flex items-center gap-1 text-xs ${
                      crecimientoPorcentaje >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {crecimientoPorcentaje >= 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      {Math.abs(crecimientoPorcentaje).toFixed(1)}%
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    ${totalIngresosHoy.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    vs. ${(totalIngresosSemana / 7).toFixed(2)} promedio diario
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-600 mb-2">Egresos de Hoy</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${egresos
                      .filter(e => moment(e.fecha).isSame(moment(), 'day'))
                      .reduce((sum, e) => sum + (parseFloat(e.monto) || 0), 0)
                      .toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Gastos del día
                  </p>
                </div>
              </div>

              <div className="h-64">
                <Line data={movimientosChartData} options={movimientosChartOptions} />
              </div>

              <div className="mt-4 text-xs text-center text-gray-500">
                Movimientos de los últimos 7 días
              </div>
            </div>
          </div>

          {/* Panel de Sugerencias - Simplificado */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-xl font-bold text-gray-800">Sugerencias Recientes</h2>
                </div>
                <Link 
                  to="/admin/centro/sugerencias"
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors duration-200 text-sm font-medium"
                >
                  Ver todas
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="p-6">
              {sugerenciasRecientes.length > 0 ? (
                <div className="space-y-3">
                  {sugerenciasRecientes.slice(0, 5).map((sugerencia) => (
                    <div
                      key={sugerencia.id_sugerencia}
                      className="p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          sugerencia.estado === 'pendiente' ? 'bg-amber-100' :
                          sugerencia.estado === 'implementada' ? 'bg-green-100' :
                          sugerencia.estado === 'revisada' ? 'bg-blue-100' :
                          'bg-red-100'
                        }`}>
                          <MessageCircle className={`w-4 h-4 ${
                            sugerencia.estado === 'pendiente' ? 'text-amber-600' :
                            sugerencia.estado === 'implementada' ? 'text-green-600' :
                            sugerencia.estado === 'revisada' ? 'text-blue-600' :
                            'text-red-600'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">
                            {sugerencia.contenido}
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          sugerencia.estado === 'pendiente' ? 'bg-amber-50 text-amber-700' :
                          sugerencia.estado === 'implementada' ? 'bg-green-50 text-green-700' :
                          sugerencia.estado === 'revisada' ? 'bg-blue-50 text-blue-700' :
                          'bg-red-50 text-red-700'
                        }`}>
                          {sugerencia.estado}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay sugerencias recientes</p>
                  <p className="text-sm text-gray-400">Las sugerencias aparecerán aquí</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="fixed top-0 left-0 right-0 z-50">
        <nav className="bg-white/80 backdrop-blur-sm border-b border-slate-100">
          <div className="max-w-full mx-auto px-4 sm:px-6">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <button 
                  className="lg:hidden p-2 mr-2 text-gray-600 hover:text-gray-900"
                  onClick={() => setIsMobileMenuOpen(true)}
                >
                  <Menu className="h-6 w-6" />
                </button>
                <Link to="/admin" className="group">
                  <div className="flex flex-col">
                    <h1 className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                      Centro Infantil
                    </h1>
                    <span className="text-xs sm:text-sm text-slate-600 font-medium">
                      {nombreCentro.split('Centro Infantil ')[1] || 'Mi Jardín'}
                    </span>
                  </div>
                </Link>
              </div>
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="relative">
                  <button
                    onClick={handleChatOpen}
                    data-chat-toggle
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                  >
                    <MessageCircle className="h-5 w-5 text-slate-800" />
                    {!showChat && mensajesNoLeidos > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {mensajesNoLeidos}
                      </div>
                    )}
                  </button>
                </div>
                <div ref={notificationsRef}>
                  <Notificaciones 
                    showNotifications={showNotifications} 
                    setShowNotifications={setShowNotifications} 
                    passwordIsCedula={passwordIsCedula}
                    userType="admin"
                    transferenciaPendientes={transferenciaPendientes}
                  />
                </div>
                <div className="relative" ref={profileMenuRef}>
                  <div 
                    className="flex items-center gap-3 group cursor-pointer"
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                  >
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-slate-600" />
                    </div>
                    <div className="hidden sm:flex flex-col">
                      <span className="text-sm font-medium text-slate-700">{user.nombre}</span>
                      <span className="text-xs text-slate-500">{user.rol}</span>
                    </div>
                  </div>
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 border border-slate-100">
                      <Link
                        to="/admin/perfil-admin"
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        Ver Perfil
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setShowProfileMenu(false);
                          handleLogout();
                        }}
                        className="lg:hidden w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-50"
                      >
                        Cerrar Sesión
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="hidden lg:block text-slate-600 hover:text-red-600 transition-all duration-300"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <div className="pt-16 flex">
        <aside className="hidden lg:fixed lg:block w-64 bg-white/80 backdrop-blur-sm border-r border-slate-100 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-4">
            <nav className="space-y-1">
              {menuItems.map((item, index) => (
                <div key={index}>
                  {item.subItems ? (
                    <div className="relative group">
                      <Link
                        to={item.href}
                        className="flex items-center gap-3 px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm transform hover:translate-x-1"
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.text}</span>
                      </Link>
                      <div className="lg:group-hover:block hidden">
                        {item.subItems.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.href}
                            className="flex items-center gap-3 px-8 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 text-sm"
                          >
                            <subItem.icon className="h-4 w-4" />
                            <span>{subItem.text}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="flex items-center gap-3 px-4 py-2.5 text-slate-600 hover:text-blue-800 hover:bg-slate-50/80 rounded-xl transition-all duration-300 text-sm transform hover:translate-x-1"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.text}</span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-500">© {new Date().getFullYear()} Mi Jardín</p>
              <p className="text-[10px] text-gray-400">Todos los derechos reservados</p>
            </div>
          </div>
        </aside>

        <main className="flex-1 lg:ml-64 p-4 sm:p-8">
          <div className="animate-fadeIn">
            {location.pathname === '/admin' ? <DashboardContent /> : <Outlet />}
          </div>
        </main>
      </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
            <div className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl overflow-y-auto">
              <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800">Menú</h2>
                <button 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1 rounded-full hover:bg-slate-100"
                >
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>
              <nav className="p-4 space-y-1">
                {menuItems.map((item, index) => (
                  <div key={index}>
                    {item.subItems ? (
                      <div className="mb-2">
                        <button
                          onClick={() => toggleSubMenu(index)}
                          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 rounded-lg transition-all duration-200 text-sm"
                        >
                          <div className="flex items-center gap-3">
                            <item.icon className="h-4 w-4" />
                            <span>{item.text}</span>
                          </div>
                          <svg
                            className={`h-4 w-4 transform transition-transform ${openSubMenus[index] ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {openSubMenus[index] && (
                          <div className="ml-6 mt-1 space-y-1">
                            {item.subItems.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.href}
                                className="flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                <subItem.icon className="h-4 w-4" />
                                <span>{subItem.text}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 rounded-lg transition-all duration-200 text-sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.text}</span>
                      </Link>
                    )}
                  </div>
                ))}
                <div className="pt-4 mt-4 border-t border-slate-100">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              </nav>
            </div>
          </div>
        )}

        {showChat && (
          <div className="fixed inset-0 z-50 flex">
            <div className="absolute inset-0" onClick={handleChatClose}></div>
            <div className="relative ml-auto">
              <Chat 
                user={user} 
                onClose={handleChatClose}
                onMessageRead={handleMessageRead}
              />
            </div>
          </div>
        )}

      {showChat && (
        <Chatbot user={user} className={showChat ? '' : 'hidden'} />
      )}
      <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-80 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-fadeInScale">
            <div className="flex items-center justify-start mb-6">
              <Lock size={28} className="text-indigo-600 mr-3" />
              <h2 className="text-2xl font-bold text-slate-800">Cambio de Contraseña Obligatorio</h2>
            </div>
            <p className="text-slate-600 mb-6 text-sm">
              Por motivos de seguridad, es necesario que establezca una nueva contraseña.
            </p>
            <form onSubmit={handlePasswordChangeSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="nuevoPasswordAdmin">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <input 
                    type={showNewPassword ? "text" : "password"}
                    id="nuevoPasswordAdmin"
                    name="nuevoPassword"
                    value={nuevoPassword}
                    onChange={(e) => setNuevoPassword(e.target.value)}
                    className={`w-full px-4 py-2.5 bg-white border rounded-lg shadow-sm focus:ring-2 transition-colors duration-150 text-sm 
                                ${passwordChangeError && (nuevoPassword === '' || !isValidPassword || passwordStrength < 75) ? 'border-red-400 ring-red-200 focus:ring-red-400 focus:border-red-400' 
                                : 'border-indigo-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    disabled={isPasswordSuccessfullyChanged}
                  />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-500 hover:text-indigo-600">
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="confirmarPasswordAdmin">
                  Confirmar Nueva Contraseña
                </label>
                <div className="relative">
                  <input 
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmarPasswordAdmin"
                    name="confirmarPassword"
                    value={confirmarPassword}
                    onChange={(e) => setConfirmarPassword(e.target.value)}
                    className={`w-full px-4 py-2.5 bg-white border rounded-lg shadow-sm focus:ring-2 transition-colors duration-150 text-sm 
                                ${passwordChangeError && confirmarPassword !== nuevoPassword ? 'border-red-400 ring-red-200 focus:ring-red-400 focus:border-red-400' 
                                : 'border-indigo-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    disabled={isPasswordSuccessfullyChanged}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-500 hover:text-indigo-600">
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {nuevoPassword && (
                <div className="space-y-2 pt-1">
                  <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength)}`}
                      style={{ width: `${passwordStrength}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-slate-600 text-right font-medium">
                    Seguridad: {getPasswordStrengthText(passwordStrength)}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1">
                    <PasswordRequirement text="Mínimo 8 caracteres" met={passwordCriteria.length} />
                    <PasswordRequirement text="Al menos una letra" met={passwordCriteria.letter} />
                    <PasswordRequirement text="Al menos un número" met={passwordCriteria.number} />
                    <PasswordRequirement text="Al menos un símbolo especial" met={passwordCriteria.special} />
                  </div>
                </div>
              )}

              {passwordChangeError && (
                <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200 text-center">
                  {passwordChangeError}
                </p>
              )}

              {apiResponseMessage.text && (
                <p className={`text-sm p-3 rounded-md border text-center 
                  ${apiResponseMessage.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' 
                                                          : 'bg-red-50 text-red-700 border-red-200'}`}>
                  {apiResponseMessage.text}
                </p>
              )}

              <button 
                type={isPasswordSuccessfullyChanged ? "button" : "submit"}
                onClick={isPasswordSuccessfullyChanged ? handleCloseModalAfterSuccess : undefined}
                disabled={!isPasswordSuccessfullyChanged && (!nuevoPassword || !confirmarPassword || !isValidPassword || passwordStrength < 75 || nuevoPassword !== confirmarPassword)}
                className={`w-full text-white font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white flex items-center justify-center 
                            ${isPasswordSuccessfullyChanged ? 'bg-slate-500 hover:bg-slate-600 focus:ring-slate-400' 
                                                            : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'}`}
              >
                <Lock size={18} className="mr-2" />
                {isPasswordSuccessfullyChanged ? 'Cerrar' : 'Cambiar Contraseña'}
              </button>
            </form>
          </div>
        </div>
      )}
      <Chatbot user={user} className={showChat ? 'hidden' : ''} />
    </div>
  );
};

export default AdminDashboard;
